import {
  ApiACLRole,
  ApiClient,
  ApiProject,
  ApiReportResponse,
  ApiScheduledReportType,
  ApiShareLinkType,
  ApiTabResponse,
} from "@incendium/api";
import {
  AccessTime,
  Assignment,
  Download,
  Delete,
  LibraryAdd,
  Send,
} from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import {
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Portal,
  Stack,
  Switch,
  TextField,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { closeSecondaryMenu, openSecondaryMenu, toggleMenu, toggleSecondaryAvailable } from "Actions";
import { authService, reportService } from "Apis";
import {
  deleteReport,
  listReportTabs,
  readReport,
  ReportBuilderSidebar,
  ReportTab,
  ReportTabTitle,
  saveReport,
  saveReportTab,
  ScheduleReportDialog,
} from "features/reports";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { useMount, useSelectedProject } from "Hooks";
import { useReports } from "Hooks/useReports";
import { useSave } from "Hooks/useSave";
import produce from "immer";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { useFromToContext } from "Providers/FromToProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { usePrevious, useUpdateEffect } from "react-use";
import { leftMenuSelector } from "Selectors/leftMenuSelector";
import { base64ToArrayBuffer, downloadZip } from "Helpers/download";
import AccessLevel from "Components/AccessLevel/AccessLevel";
import { PlatformURL } from "config";
import { useFilterableDimensions } from "features/analytics";
import CreateNameDialog from "Components/CreateNameDialog/CreateNameDialog";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { PageTitle } from "consts";
import withDatepicker from "HoC/withDatepicker";
import { useNotification } from "Hooks";
import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";

export const ReportTabPanel = ({
  value,
  index,
  project,
  client,
  report,
  tab,
  editMode,
  setEditMode,
}: {
  children?: React.ReactNode;
  index: any;
  value: any;
  project: ApiProject;
  client: ApiClient;
  report: ApiReportResponse;
  tab: ApiTabResponse;
  editMode: boolean | null;
  setEditMode: React.Dispatch<React.SetStateAction<boolean | null>>;
}) => {
  return (
    <Box role="tabpanel" hidden={value !== index}>
      {value === index && (

        <ReportTab
          project={project}
          client={client}
          report={report}
          tab={tab}
          editMode={editMode}
          setEditMode={setEditMode}
          activeTab={value}
        />

      )}
    </Box>
  );
};

interface IReportPageProps {
  readOnly?: boolean;
  disableEditMode?: boolean;
  passedReportID?: number;
  passedProject?: ApiProject;
  passedClient?: ApiClient;
}

function ReportPage({
  readOnly,
  disableEditMode,
  passedReportID,
  passedProject,
  passedClient,
}: IReportPageProps) {
  const dispatch = useDispatch();
  const { setReports } = useReports();
  const { save } = useSave();
  const { reportId } = useParams<{ reportId: string }>();
  const { selectedProject, selectedClient } = useSelectedProject();
  const [report, setReport] = useState<ApiReportResponse>({});
  const [edit, setEdit] = useState(false);
  const [editMode, setEditMode] = useAppendSearchState("re", false);
  const prevEditMode = usePrevious(editMode);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [tab, setTab] = useAppendSearchState<number>("rt", 0);
  const [newName, setNewName] = useState(report.name);
  const [tabs, setTabs] = useState<ApiTabResponse[]>([]);
  const { from, to, lastNDays } = useFromToContext();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { isSecondaryOpen, isMenuOpen, isSecondaryAvailable } = useSelector(leftMenuSelector);
  const [isExporting, setIsExporting] = useState(false);
  const { } = useFilterableDimensions(); // pre cache filters
  const [initialMenuOpen] = useState(isMenuOpen);
  const [tabOpen, setTabOpen] = useState(false);
  const history = useHistory();
  const project = useMemo(
    () => passedProject || selectedProject,
    [selectedProject, passedProject]
  );
  const client = useMemo(
    () => passedClient || selectedClient,
    [selectedClient, passedClient]
  );

  useMount(() => {
    const rID = passedReportID || reportId;
    if (!rID) {
      return;
    }

    // setEditMode(query.has("edit"));
    const load = async () => {
      const res = await readReport(project?.id as number, Number(rID));
      setReport(res);

      const tabsRes = await listReportTabs(project?.id as number, Number(rID));
      setTabs(tabsRes.results || []);
    };
    load();
  });

  useEffect(() => {
    setNewName(report.name);
  }, [edit]);

  const saveNewName = () => {
    setReport(
      produce(report, (draft) => {
        draft.name = newName;
      })
    );
  };

  useUpdateEffect(() => {
    if (!report?.id) {
      return;
    }
    onSaveReport(report);
  }, [report]);

  useEffect(() => {
    if (editMode && !prevEditMode) {
      dispatch(openSecondaryMenu());
      if (!isSecondaryAvailable) {
        dispatch(toggleSecondaryAvailable());
      }
      if (!isMenuOpen) {
        dispatch(toggleMenu());
      }

      return;
    }
    if (!editMode) {
      if (isSecondaryAvailable) {
        dispatch(toggleSecondaryAvailable());
      }
      if (isSecondaryOpen) {
        dispatch(closeSecondaryMenu());
      }

      if (!initialMenuOpen && isMenuOpen) {
        dispatch(toggleMenu());
      }
      return;
    }
  }, [
    editMode,
    dispatch,
    initialMenuOpen,
    isMenuOpen,
    isSecondaryOpen,
    prevEditMode,
    isSecondaryAvailable,
  ]);

  const onSaveReport = async (report: ApiReportResponse) => {
    if (!editMode) {
      return;
    }
    save(async () => {
      const res = await saveReport(project?.id as number, report);
      setReports((prevReports) =>
        produce(prevReports, (draft) => {
          const idx = draft.findIndex((r) => r.id === res.id);
          if (idx >= 0) {
            draft[idx] = res;
          }
        })
      );

      setEdit(false);
    });
  };

  const addTab = async (name: string) => {
    if (!editMode) {
      return;
    }
    save(async () => {
      const res = await saveReportTab(
        project?.id as number,
        report.id as number,
        {
          name,
          order: tabs.length,
        }
      );

      setTabs(
        produce(tabs, (draft) => {
          draft.push(res);
        })
      );
    });
  };

  const onRemoveTab = (reportTab: ApiTabResponse) => {
    openConfirmation({
      title: `Are you sure you want to delete ${reportTab.name}`,
      body: `This action can not be undone`,
      callback: async () => {
        try {
          await reportService.reportServiceDeleteTab({
            projectId: project?.id as number,
            reportId: report.id as number,
            tabId: reportTab.id as number,
          });

          const idx = tabs.findIndex((d) => d.id === reportTab.id);
          setTabs(
            produce(tabs, (draft) => {
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            })
          );
          setTab(
            tab !== idx ? tab : tab === 0 && tabs.length > 1 ? tab + 1 : tab - 1
          );

          showSuccessNotification(`${reportTab.name} Deleted`);

          closeConfirmation();
        } catch (error) {
          showErrorNotification(
            `Error Removing ${reportTab.name}, please try again`
          );
        }
      },
    });
  };

  const onDeleteReport = async () => {
    openConfirmation({
      title: `Are you sure you want to delete this Report`,
      body: `This action can not be undone`,
      callback: async () => {
        try {
          await deleteReport(project?.id as number, report.id as number);
          setReports(reports =>
            produce(reports, (draft) => {
              const idx = draft.findIndex((r) => r.id === report.id);
              if (idx !== -1) draft.splice(idx, 1);
            })
          );
          showSuccessNotification(`${report.name} Deleted`);
          history.push("./../reports");
          closeConfirmation();
        } catch (error) {
          showErrorNotification(`Internal Error deleting report, please try again`);
        }
      },
    });
  };

  const onExport = async () => {
    setIsExporting(true);
    try {
      const res = await reportService.reportServiceReadReportZIP({
        projectId: project?.id as number,
        reportId: report.id as number,
        payload: {
          from: from ? from?.clone().utc().startOf("day").toDate() : undefined,
          to: to ? to?.clone().utc().endOf("day").toDate() : undefined,
          lastNDays: lastNDays ? lastNDays : undefined,
          timezone: client?.timezone,
          type: ApiScheduledReportType.SCHEDULED_REPORT_TYPE_CSV_AND_XLS,
        },
      });
      if (res?.result?.result) {
        const buffer = base64ToArrayBuffer(res.result.result);
        downloadZip(buffer, report.name || "Report Export");
      }
    } catch (error) {
      showErrorNotification("Internal Server Error");
    }

    setIsExporting(false);
  };
  const share = async () => {
    const res = await authService.authShareToken({
      body: {
        clientId: client?.id as number,
        projectId: project?.id as number,
        reportId: report.id as number,
        timezone: client?.timezone || undefined,
        from: from ? from?.clone().utc().startOf("day").toDate() : undefined,
        to: to ? to?.clone().utc().endOf("day").toDate() : undefined,
        lastNDays: lastNDays ? lastNDays : undefined,
        type: ApiShareLinkType.SHARE_LINK_TYPE_REPORT,
      },
    });

    openConfirmation({
      title: `Use this link`,
      body: (
        <Stack spacing={2}>
          <Button
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(
                `${PlatformURL}/reports/${res.token}`
              );
            }}
            endIcon={<Assignment />}
          >
            Copy Link
          </Button>

          <Typography
            variant="caption"
            noWrap
          >{`${PlatformURL}/reports/${res.token}`}</Typography>
        </Stack>
      ),
      callback: async () => {
        closeConfirmation();
      },
    });
  };

  const toggelEditMode = useCallback(
    (checked: boolean) => {
      setEditMode(checked);
      // if (checked) {
      //   const newUrl = `${reportId}?edit`;
      //   window.history.replaceState(
      //     {
      //       ...window.history.state,
      //       as: newUrl,
      //       url: newUrl,
      //     },
      //     "",
      //     newUrl
      //   );
      // } else {
      //   window.history.replaceState({}, "", `${reportId}`);
      // }
    },
    [reportId]
  );

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: number | null,
  ) => {
    if (value === null) {
      return;
    }
    setTab(value);
  };

  if (!project || !client || !report || !report.id) {
    return <CircularProgress />;
  }

  return (
    <>
      {editMode && <ReportBuilderSidebar />}
      <Portal container={() => document.getElementById(PageTitle)}>
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {edit ? (
            <>
              <Box mr={1} sx={{ minWidth: 250 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Report Name"
                  value={newName || ""}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </Box>
              <Box mr={1}>
                <Button onClick={saveNewName}>Save</Button>
              </Box>
              <Button color="secondary" onClick={() => setEdit(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Stack direction="row" spacing={2} alignItems={"center"}>
              <Stack direction="row" alignItems={"center"}>
                <Typography variant="h1">{report.name}</Typography>
                {editMode && (
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => setEdit(true)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              {!disableEditMode && (
                <AccessLevel role={ApiACLRole.ANALYTICS_ADMIN} write>
                  <Stack direction="row" alignItems="center" spacing={2} >
                    <StyledFlexBox ml={2}>
                      <Box display="flex" alignItems="center">
                        <Switch
                          size="small"
                          checked={editMode || false}
                          onChange={(e, checked) => toggelEditMode(checked)}
                        />
                        <Typography color={editMode ? "primary" : "secondary"}>
                          Edit Mode
                        </Typography>
                      </Box>
                    </StyledFlexBox>
                    {editMode && (
                      <Button startIcon={<Delete />} size="small" color="secondary" onClick={() => onDeleteReport()}>Delete Report</Button>
                    )}
                  </Stack>
                </AccessLevel>
              )}
            </Stack>
          )}
        </Box>
      </Portal>

      <Box
        sx={{
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px - 85px)`,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            width: "100%",
            minWidth: 0,
            flexShrink: 0,
            pb: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {(editMode || tabs.length > 1) && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                <ToggleButtonGroup
                  value={tab}
                  exclusive
                  color="primary"
                  onChange={handleTabChange}
                  sx={{
                    display: "inline-flex",
                    flexWrap: "nowrap",
                    flexShrink: 0,
                    width: "max-content",
                    maxWidth: "none",
                  }}
                >
                  {tabs.map((t, i) => (
                    <ReportTabTitle
                      key={t.id ?? i}
                      index={i}
                      project={project}
                      report={report}
                      reportTab={t}
                      tabs={tabs}
                      setTabs={setTabs}
                      editMode={editMode || false}
                      onRemoveTab={onRemoveTab}
                      onSelect={() => setTab(i)}
                    />
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {editMode && (
              <IconButton
                color="primary"
                onClick={() => setTabOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                <LibraryAdd />
              </IconButton>
            )}
          </Stack>

          <AccessLevel
            role={ApiACLRole.ANALYTICS_ADMIN}
            write
            noAccess={
              <Button
                variant="outlined"
                color="secondary"
                onClick={onExport}
                disabled={isExporting}
                endIcon={
                  isExporting ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Download />
                  )
                }
              >
                Export
              </Button>
            }
          >
            <Box sx={{ flexShrink: 0 }}>
              <ButtonGroup>
                <Button
                  color="secondary"
                  onClick={onExport}
                  disabled={isExporting}
                  endIcon={
                    isExporting ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <Download />
                    )
                  }
                >
                  Export
                </Button>

                <Button
                  color="secondary"
                  onClick={() => setScheduleOpen(true)}
                  endIcon={<AccessTime />}
                >
                  Schedule Report
                </Button>
                <Button color="secondary" onClick={share} endIcon={<Send />}>
                  Share
                </Button>
              </ButtonGroup>
            </Box>
          </AccessLevel>
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {tabs.map((t, i) => (
            <ReportTabPanel
              key={t.id ?? i}
              value={tab}
              index={i}
              project={project}
              client={client}
              report={report}
              tab={t}
              editMode={editMode}
              setEditMode={setEditMode}
            >
              {t.name}
            </ReportTabPanel>
          ))}
        </Box>
      </Box>
      <ScheduleReportDialog
        open={scheduleOpen}
        setOpen={setScheduleOpen}
        project={project}
        report={report}
      />
      <CreateNameDialog
        open={tabOpen}
        setOpen={setTabOpen}
        title="Add a new tab"
        onSaved={addTab}
      />
    </>
  );
}

export default withDatepicker(ReportPage);
